import {
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { OcpImageComponent } from '../../atoms/image/image.component';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

@Component({
  selector: 'ocp-scene-thumbnail',
  imports: [OcpImageComponent, TranslatePipe, ThemedIconComponent],
  templateUrl: './scene-thumbnail.component.html',
  styleUrl: './scene-thumbnail.component.scss',
  host: {
    class: 'scene-thumbnail',
  },
})
export class SceneThumbnailComponent {
  /** Base64 data URL or HTTP URL of the scene thumbnail image. */
  readonly thumbnailUrl = input<string | null | undefined>(null);

  /** Accessible label for the thumbnail image. */
  readonly alt = input<string>('');

  /** Marks the thumbnail as high-priority when it is expected to be the LCP image. */
  readonly priority = input(false, { transform: booleanAttribute });

  private readonly destroyRef = inject(DestroyRef);
  private readonly documentRef = inject(DOCUMENT);
  private readonly refreshNonce = signal(0);
  private readonly renderedThumbnailUrl = signal<string | null>(null);
  private refreshRetryTimers: ReturnType<typeof globalThis.setTimeout>[] = [];

  /** Whether the thumbnail is available. */
  protected readonly hasImage = computed(() => !!this.resolvedThumbnailUrl());

  /** Whether the URL is a data URL (base64). NgOptimizedImage doesn't support data URLs. */
  protected readonly isDataUrl = computed(
    () => this.resolvedThumbnailUrl()?.startsWith('data:') ?? false
  );

  /**
   * Adds a local nonce to non-data URLs to force the browser to request and repaint
   * the image when the source changes or when the tab/window becomes visible again.
   */
  protected readonly resolvedThumbnailUrl = computed(() => {
    const url = this.renderedThumbnailUrl();
    if (!url) {
      return null;
    }

    if (url.startsWith('data:')) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}ui-nonce=${this.refreshNonce()}`;
  });

  constructor() {
    effect(() => {
      const sourceUrl = this.thumbnailUrl() ?? null;
      this.clearRefreshRetries();

      if (!sourceUrl) {
        this.renderedThumbnailUrl.set(null);
        return;
      }

      if (sourceUrl.startsWith('data:')) {
        this.renderedThumbnailUrl.set(sourceUrl);
        return;
      }

      // Keep the current image mounted to avoid blank flashes.
      this.renderedThumbnailUrl.set(sourceUrl);
      this.scheduleRefreshRetries(sourceUrl);
    });

    const forceRefresh = () => {
      const url = this.renderedThumbnailUrl();
      if (url && !url.startsWith('data:')) {
        this.refreshNonce.update(value => value + 1);
      }
    };

    const onVisibilityChange = () => {
      if (this.documentRef.visibilityState === 'visible') {
        forceRefresh();
      }
    };

    const windowRef = this.documentRef.defaultView;
    this.destroyRef.onDestroy(() => {
      this.clearRefreshRetries();
      if (!windowRef) {
        return;
      }

      windowRef.removeEventListener('focus', forceRefresh);
      this.documentRef.removeEventListener('visibilitychange', onVisibilityChange);
    });

    if (!windowRef) {
      return;
    }

    windowRef.addEventListener('focus', forceRefresh);
    this.documentRef.addEventListener('visibilitychange', onVisibilityChange);
  }

  private scheduleRefreshRetries(sourceUrl: string): void {
    // Delay first refresh slightly so backend thumbnail updates can settle,
    // then retry a few times to handle eventual consistency.
    const retryDelaysMs = [150, 700, 1800, 3200];
    this.refreshRetryTimers = retryDelaysMs.map(delay =>
      globalThis.setTimeout(() => {
        if (this.thumbnailUrl() === sourceUrl && this.renderedThumbnailUrl() === sourceUrl) {
          this.refreshNonce.update(value => value + 1);
        }
      }, delay)
    );
  }

  private clearRefreshRetries(): void {
    this.refreshRetryTimers.forEach(timerId => globalThis.clearTimeout(timerId));
    this.refreshRetryTimers = [];
  }
}
